import { ApiError } from '../lib/api'

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  (typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : 'http://127.0.0.1:8000/api/v1')

/**
 * Explain My Chart client — adapted to the REAL backend contract
 * (apps/api/app/schemas/explain.py — audit 2026-08-30 critical #3):
 * responses carry `explanation_text` + `citations`, NOT `summary`/`sources`.
 *
 * There is NO fabricated fallback content in this module: when the backend
 * fails we throw and the UI renders an honest error with a retry affordance
 * (ADR-018 Q10 — every explanation must cite its chart sections, and invented
 * citations are worse than none).
 */

export type ExplainLanguage = 'en' | 'ar'

export type ExplanationCitation = {
  section: string
  items_used: string[]
}

export type ChartExplanation = {
  explanation_id: string
  patient_id: string
  language: string
  question: string | null
  explanation_text: string
  citations: ExplanationCitation[]
  audio_url: string | null
  chart_revision: string | null
  cache_hit: boolean
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  }
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (!response.ok) {
    let detail: unknown = response.statusText
    try {
      const body = await response.json()
      detail = body.detail ?? body
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, detail)
  }
  return (await response.json()) as T
}

function normalizeExplanation(raw: Partial<ChartExplanation> & { audio_base64?: string | null }, patientId: string, language: ExplainLanguage): ChartExplanation {
  return {
    explanation_id: raw.explanation_id ?? '',
    patient_id: raw.patient_id ?? patientId,
    language: raw.language ?? language,
    question: raw.question ?? null,
    explanation_text: raw.explanation_text ?? '',
    citations: raw.citations ?? [],
    audio_url: raw.audio_url ?? (raw.audio_base64 ? `data:audio/wav;base64,${raw.audio_base64}` : null),
    chart_revision: raw.chart_revision ?? null,
    cache_hit: raw.cache_hit ?? false,
  }
}

export async function fetchExplainSummary(patientId: string, language: ExplainLanguage): Promise<ChartExplanation> {
  const raw = await requestJson<Partial<ChartExplanation> & { audio_base64?: string | null }>(
    `/explain/${patientId}/summary?lang=${language}`,
  )
  return normalizeExplanation(raw, patientId, language)
}

export async function askExplainQuestion(
  patientId: string,
  payload: { question: string; language: ExplainLanguage },
): Promise<ChartExplanation> {
  const raw = await requestJson<Partial<ChartExplanation> & { audio_base64?: string | null }>(
    `/explain/${patientId}/ask`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  )
  return normalizeExplanation(raw, patientId, payload.language)
}
